import dayjs from "dayjs";
import {
  BenchTenderShape,
  type CreateOutboundBenchOrdersInput,
} from "../../generated/graphql";

/**
 * The service level and dates the operator picks for new test orders. Dates
 * are `YYYY-MM-DD` and times `HH:mm`, the values the date and time inputs
 * hold; an empty string leaves that field out of the tender.
 */
export type BenchOrderSchedule = {
  serviceLevelCode: string;
  pickupDate: string;
  deliveryDate: string;
  pickupStart: string;
  pickupEnd: string;
  deliveryStart: string;
  deliveryEnd: string;
};

const DATE_FORMAT = "YYYY-MM-DD";
const DEFAULT_WINDOW_START = "08:00";
const DEFAULT_WINDOW_END = "10:00";

/** Today for the pickup, tomorrow for the delivery, both 08:00 to 10:00. */
export const defaultSchedule = (today: Date): BenchOrderSchedule => ({
  serviceLevelCode: "",
  pickupDate: dayjs(today).format(DATE_FORMAT),
  deliveryDate: dayjs(today).add(1, "day").format(DATE_FORMAT),
  pickupStart: DEFAULT_WINDOW_START,
  pickupEnd: DEFAULT_WINDOW_END,
  deliveryStart: DEFAULT_WINDOW_START,
  deliveryEnd: DEFAULT_WINDOW_END,
});

/** `HH:mm` as the backend takes it, `HHmm`; undefined when empty. */
const toHhmm = (time: string): string | undefined =>
  time === "" ? undefined : time.replace(":", "");

const orUndefined = (value: string): string | undefined =>
  value === "" ? undefined : value;

/**
 * The schedule fields of the create input. A shape without a pickup sends no
 * pickup fields, and one without a delivery sends no delivery fields, since
 * the tender has no party to carry them.
 */
export const scheduleInput = (
  schedule: BenchOrderSchedule,
  shape: BenchTenderShape,
): Pick<
  CreateOutboundBenchOrdersInput,
  | "serviceLevelCode"
  | "pickupDate"
  | "deliveryDate"
  | "pickupAppointmentStart"
  | "pickupAppointmentEnd"
  | "deliveryAppointmentStart"
  | "deliveryAppointmentEnd"
> => {
  const hasPickup = shape !== BenchTenderShape.Delivery;
  const hasDelivery = shape !== BenchTenderShape.Pickup;
  return {
    serviceLevelCode: orUndefined(schedule.serviceLevelCode),
    ...(hasPickup
      ? {
          pickupDate: orUndefined(schedule.pickupDate),
          pickupAppointmentStart: toHhmm(schedule.pickupStart),
          pickupAppointmentEnd: toHhmm(schedule.pickupEnd),
        }
      : {}),
    ...(hasDelivery
      ? {
          deliveryDate: orUndefined(schedule.deliveryDate),
          deliveryAppointmentStart: toHhmm(schedule.deliveryStart),
          deliveryAppointmentEnd: toHhmm(schedule.deliveryEnd),
        }
      : {}),
  };
};

/** Shown under the appointment fields: the inbound handler's known limit. */
export const APPOINTMENT_DAY_NOTE =
  "The EDI inbound handler places appointment times on the day it processes the tender, so the window lands on today whatever the date. The date sets the service date.";
